import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import StarField from '../components/StarField';
import { StarDust, AuroraRings, CornerDecorations } from '../components/CosmicEffects';
import { archetypes } from '../data/archetypes';
import styles from './LiveHost.module.css';

export default function LiveHost() {
  const [roomId, setRoomId] = useState(null);
  const [participantCount, setParticipantCount] = useState(0);
  const [results, setResults] = useState([]);
  const [currentResult, setCurrentResult] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [hostIp, setHostIp] = useState(() => {
    // 从 localStorage 读取上次保存的 IP
    return localStorage.getItem('liveHostIp') || '';
  });
  const [showIpInput, setShowIpInput] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null); // 点击星球显示的详情
  const wsRef = useRef(null);
  const resultQueueRef = useRef([]);
  const isShowingRef = useRef(false);

  // 生成扫码链接
  const getJoinUrl = useCallback(() => {
    if (process.env.NODE_ENV === 'production') {
      return `${window.location.origin}?live=${roomId}`;
    }
    // 开发环境：如果设置了IP就用IP，否则用当前hostname
    const host = hostIp || window.location.hostname;
    const port = window.location.port || '3000';
    return `http://${host}:${port}?live=${roomId}`;
  }, [roomId, hostIp]);

  // 保存IP到localStorage
  const handleSaveIp = (ip) => {
    setHostIp(ip);
    localStorage.setItem('liveHostIp', ip);
    setShowIpInput(false);
  };

  // 显示下一个结果
  const showNextResult = useCallback(() => {
    if (resultQueueRef.current.length === 0) {
      isShowingRef.current = false;
      setShowResult(false);
      setCurrentResult(null);
      return;
    }
    
    isShowingRef.current = true;
    const next = resultQueueRef.current.shift();
    setCurrentResult(next);
    setShowResult(true);
    
    // 8秒后显示下一个或隐藏
    setTimeout(() => {
      showNextResult();
    }, 8000);
  }, []);

  // 添加新结果到队列
  const addResult = useCallback((result) => {
    setResults(prev => [result, ...prev].slice(0, 50));
    resultQueueRef.current.push(result);
    
    if (!isShowingRef.current) {
      showNextResult();
    }
  }, [showNextResult]);

  useEffect(() => {
    // 连接 WebSocket
    const getWsUrl = () => {
      if (process.env.REACT_APP_ORACLE_WS) {
        return process.env.REACT_APP_ORACLE_WS;
      }
      if (process.env.REACT_APP_ORACLE_API) {
        return process.env.REACT_APP_ORACLE_API.replace(/^https?:\/\//, (match) =>
          match === 'https://' ? 'wss://' : 'ws://'
        );
      }
      return process.env.NODE_ENV === 'production'
        ? `wss://${window.location.host}`
        : 'ws://localhost:3001';
    };

    const wsUrl = getWsUrl();
    
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({ type: 'create_room' }));
    };

    ws.onmessage = (event) => {
      const msg = JSON.parse(event.data);
      
      switch (msg.type) {
        case 'room_created':
          setRoomId(msg.roomId);
          break;
        case 'participant_joined':
        case 'participant_left':
          setParticipantCount(msg.count);
          break;
        case 'new_result':
          addResult(msg.result);
          break;
        default:
          break;
      }
    };

    return () => {
      ws.close();
    };
  }, [addResult]);

  if (!roomId) {
    return (
      <div className={styles.container}>
        <StarField density={100} speed={0.2} />
        <div className={styles.loading}>正在创建房间...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <StarField density={80} speed={0.15} />
      <StarDust count={35} />
      <CornerDecorations />

      {/* 顶部信息栏 */}
      <div className={styles.header}>
        <div className={styles.headerGlow} />
        <div className={styles.roomInfo}>
          <span className={styles.roomLabel}>✦ 房间号</span>
          <span className={styles.roomId}>{roomId}</span>
        </div>
        <div className={styles.participantInfo}>
          <div className={styles.countWrapper}>
            <span className={styles.count}>{participantCount}</span>
            <span className={styles.countGlow}>{participantCount}</span>
          </div>
          <span className={styles.label}>人参与</span>
        </div>
      </div>

      {/* 主内容区 */}
      <div className={styles.main}>
        {/* 左侧：二维码 */}
        <div className={styles.qrSection}>
          <div className={styles.qrDecoration}>
            <AuroraRings />
          </div>
          <h2 className={styles.qrTitle}>
            <span className={styles.titleIcon}>🌌</span>
            扫码参与测试
          </h2>
          <div className={styles.qrWrapper}>
            <div className={styles.qrCorner} data-pos="tl" />
            <div className={styles.qrCorner} data-pos="tr" />
            <div className={styles.qrCorner} data-pos="bl" />
            <div className={styles.qrCorner} data-pos="br" />
            <QRCodeSVG 
              value={getJoinUrl()} 
              size={280}
              bgColor="transparent"
              fgColor="#ffffff"
              level="H"
            />
          </div>
          <p className={styles.qrHint}>✨ 探索你的行星原型 ✨</p>
          <p className={styles.qrUrl}>{getJoinUrl()}</p>
          <button 
            className={styles.ipBtn} 
            onClick={() => setShowIpInput(true)}
          >
            ⚙️ 设置局域网IP
          </button>
        </div>

        {/* IP设置弹窗 */}
        {showIpInput && (
          <div className={styles.ipModal}>
            <div className={styles.ipModalContent}>
              <h3>设置局域网IP</h3>
              <p>请输入电脑的局域网IP地址（如 192.168.1.100）</p>
              <input
                type="text"
                className={styles.ipInput}
                defaultValue={hostIp}
                placeholder="192.168.x.x"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveIp(e.target.value);
                  }
                }}
              />
              <div className={styles.ipBtns}>
                <button onClick={() => setShowIpInput(false)}>取消</button>
                <button onClick={(e) => {
                  const input = e.target.parentElement.previousElementSibling;
                  handleSaveIp(input.value);
                }}>保存</button>
              </div>
            </div>
          </div>
        )}

        {/* 右侧：结果展示区域 - 包含历史星球和当前结果 */}
        <div className={styles.resultSection}>
          {/* 星云背景 */}
          <div className={styles.nebulaBackground} />
          
          {/* 轨道装饰 */}
          <div className={styles.orbitRings}>
            <div className={styles.orbitRing} />
            <div className={styles.orbitRing} data-size="md" />
            <div className={styles.orbitRing} data-size="lg" />
          </div>
          
          {/* 历史结果星球 - 散布在这个区域内 */}
          <div className={styles.galaxyField}>
            {results.slice(showResult ? 1 : 0, 30).map((r, i) => {
              const arch = archetypes[r.topArchetype] || {};
              return (
                <FloatingPlanet 
                  key={r.participantId + r.timestamp} 
                  archetype={arch}
                  result={r}
                  index={i}
                  onClick={() => setSelectedResult(r)}
                />
              );
            })}
          </div>

          {/* 当前结果展示 */}
          {showResult && currentResult ? (
            <StarRevealResult result={currentResult} key={currentResult.participantId + currentResult.timestamp} />
          ) : (
            <div className={styles.waitingResults}>
              <div className={styles.waitingOrb}>
                <div className={styles.waitingOrbInner} />
                <div className={styles.waitingOrbRing} />
              </div>
              <div className={styles.waitingIcon}>✦</div>
              <p>等待参与者完成测试...</p>
              <div className={styles.waitingHint}>扫描左侧二维码开始探索</div>
            </div>
          )}
        </div>
      </div>

      {/* 底部装饰 */}
      <div className={styles.bottomDecoration}>
        <div className={styles.bottomLine} />
        <span className={styles.bottomText}>🌟 行星原型探索 · 发现你的宇宙身份 🌟</span>
        <div className={styles.bottomLine} />
      </div>

      {/* 星球详情弹窗 */}
      {selectedResult && (
        <PlanetDetailModal 
          result={selectedResult} 
          onClose={() => setSelectedResult(null)} 
        />
      )}
    </div>
  );
}

// 星空浮现结果组件
function StarRevealResult({ result }) {
  const archetype = archetypes[result.topArchetype] || {};
  const [phase, setPhase] = useState('stars'); // stars -> reveal -> show

  useEffect(() => {
    const timer1 = setTimeout(() => setPhase('reveal'), 800);
    const timer2 = setTimeout(() => setPhase('show'), 1600);
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, [result]);

  // 生成随机星星
  const stars = useMemo(() => {
    return Array.from({ length: 50 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 2.5 + 1,
      delay: Math.random() * 0.5,
      duration: Math.random() * 1 + 1.5,
    }));
  }, []);

  return (
    <div className={styles.starRevealContainer}>
      {/* 星球展示区 */}
      <div className={styles.planetArea}>
        {/* 背景星星层 */}
        <div className={styles.starsLayer}>
          {stars.map((star) => (
            <div
              key={star.id}
              className={`${styles.star} ${phase !== 'stars' ? styles.starFade : ''}`}
              style={{
                left: `${star.x}%`,
                top: `${star.y}%`,
                width: star.size,
                height: star.size,
                animationDelay: `${star.delay}s`,
                animationDuration: `${star.duration}s`,
              }}
            />
          ))}
        </div>

        {/* 聚集光效 */}
        <div className={`${styles.gatherLight} ${phase !== 'stars' ? styles.gatherActive : ''}`} />

        {/* 星球浮现 */}
        <div className={`${styles.planetReveal} ${phase === 'show' ? styles.planetShow : ''}`}>
          {archetype.planetImg ? (
            <img src={archetype.planetImg} alt={archetype.name} className={styles.revealPlanetImg} />
          ) : (
            <div className={styles.revealPlanetEmoji}>{archetype.planet || '🪐'}</div>
          )}
          {/* 光晕效果 */}
          <div
            className={styles.planetGlow}
            style={{
              background: `radial-gradient(circle, ${archetype.color || '#a78bfa'}35 0%, transparent 70%)`,
            }}
          />
        </div>
      </div>

      {/* 结果信息 */}
      <div className={`${styles.resultInfo} ${phase === 'show' ? styles.resultInfoShow : ''}`}>
        <h2 className={styles.revealName}>{archetype.name || result.topArchetype}</h2>
        <p className={styles.revealPlanetName}>{archetype.planet}</p>
        <p className={styles.revealFeature}>{archetype.feature?.slice(0, 50)}...</p>

        {/* 分数条 */}
        <div className={styles.revealScores}>
          {Object.entries(result.scores)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 3)
            .map(([key, value]) => (
              <div key={key} className={styles.revealScoreItem}>
                <span className={styles.revealScoreName}>{archetypes[key]?.name || key}</span>
                <div className={styles.revealScoreBar}>
                  <div
                    className={styles.revealScoreFill}
                    style={{
                      width: `${Math.min(value * 10, 100)}%`,
                      background: archetypes[key]?.color || '#a78bfa',
                    }}
                  />
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

// 结果卡片组件（保留作为备用）
function ResultCard({ result }) {
  const archetype = archetypes[result.topArchetype] || {};
  
  return (
    <div className={styles.resultCard}>
      <div className={styles.resultPlanet}>{archetype.planet || '🪐'}</div>
      <h2 className={styles.resultName}>{archetype.name || result.topArchetype}</h2>
      <p className={styles.resultFeature}>{archetype.feature?.slice(0, 80)}...</p>
      <div className={styles.resultScores}>
        {Object.entries(result.scores)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5)
          .map(([key, value]) => (
            <div key={key} className={styles.scoreBar}>
              <span className={styles.scoreName}>{archetypes[key]?.name || key}</span>
              <div className={styles.scoreTrack}>
                <div 
                  className={styles.scoreFill} 
                  style={{ width: `${Math.min(value * 10, 100)}%` }}
                />
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

// 漂浮星球组件 - 在结果区域内随机分布
function FloatingPlanet({ archetype, result, index, onClick }) {
  const style = useMemo(() => {
    // 黄金角度螺旋分布
    const goldenAngle = Math.PI * (3 - Math.sqrt(5));
    const angle = index * goldenAngle;
    
    // 螺旋分布，从中心向外扩展
    const radius = 15 + Math.sqrt(index) * 12;
    
    // 转换为x,y坐标
    const x = 50 + radius * Math.cos(angle);
    const y = 50 + radius * Math.sin(angle);
    
    // 大小和透明度
    const size = Math.max(25, 42 - index * 0.8);
    const opacity = Math.max(0.4, 0.8 - index * 0.02);
    const duration = 15 + (index % 6) * 3;
    const delay = index * 0.1;
    
    return {
      left: `${Math.max(5, Math.min(95, x))}%`,
      top: `${Math.max(5, Math.min(95, y))}%`,
      width: size,
      height: size,
      opacity,
      animationDuration: `${duration}s`,
      animationDelay: `${delay}s`,
      zIndex: 20 - index,
    };
  }, [index]);

  return (
    <div className={styles.floatingPlanet} style={style} onClick={onClick}>
      {archetype.planetImg ? (
        <img 
          src={archetype.planetImg} 
          alt={archetype.name}
          className={styles.floatingPlanetImg}
        />
      ) : (
        <span className={styles.floatingPlanetEmoji}>{archetype.planet || '🪐'}</span>
      )}
      <div 
        className={styles.floatingPlanetGlow}
        style={{ background: `radial-gradient(circle, ${archetype.color || '#a78bfa'}20 0%, transparent 70%)` }}
      />
    </div>
  );
}


// 星球详情弹窗组件
function PlanetDetailModal({ result, onClose }) {
  const archetype = archetypes[result.topArchetype] || {};
  
  // 生成装饰粒子
  const particles = useMemo(() => {
    return Array.from({ length: 20 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 3 + 1,
      duration: Math.random() * 3 + 2,
      delay: Math.random() * 2,
    }));
  }, []);
  
  // 点击背景关闭
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className={styles.detailModal} onClick={handleBackdropClick}>
      <div className={styles.detailContent}>
        {/* 漂浮粒子 */}
        <div className={styles.particlesLayer}>
          {particles.map((p) => (
            <div
              key={p.id}
              className={styles.particle}
              style={{
                left: `${p.x}%`,
                top: `${p.y}%`,
                width: p.size,
                height: p.size,
                animationDuration: `${p.duration}s`,
                animationDelay: `${p.delay}s`,
              }}
            />
          ))}
        </div>

        {/* 关闭按钮 */}
        <button className={styles.detailClose} onClick={onClose}>×</button>
        
        {/* 星球图标 */}
        <div className={styles.detailPlanet}>
          {/* 小卫星 */}
          <div className={styles.satellite} style={{ '--color': archetype.color || '#a78bfa' }} />
          <div className={styles.satellite2} style={{ '--color': archetype.color || '#60a5fa' }} />
          
          {archetype.planetImg ? (
            <img src={archetype.planetImg} alt={archetype.name} className={styles.detailPlanetImg} />
          ) : (
            <span className={styles.detailPlanetEmoji}>{archetype.planet || '🪐'}</span>
          )}
          <div 
            className={styles.detailPlanetGlow}
            style={{ background: `radial-gradient(circle, ${archetype.color || '#a78bfa'}40 0%, transparent 70%)` }}
          />
        </div>

        {/* 星球名称 */}
        <h2 className={styles.detailName}>{archetype.name || result.topArchetype}</h2>
        <p className={styles.detailPlanetLabel}>{archetype.planet}</p>

        {/* 特征描述 */}
        <p className={styles.detailFeature}>{archetype.feature}</p>

        {/* 分数雷达 */}
        <div className={styles.detailScores}>
          <h4>能量分布</h4>
          {Object.entries(result.scores)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5)
            .map(([key, value]) => (
              <div key={key} className={styles.detailScoreItem}>
                <span className={styles.detailScoreName}>{archetypes[key]?.name || key}</span>
                <div className={styles.detailScoreBar}>
                  <div
                    className={styles.detailScoreFill}
                    style={{
                      width: `${Math.min(value * 10, 100)}%`,
                      background: archetypes[key]?.color || '#a78bfa',
                    }}
                  />
                </div>
                <span className={styles.detailScoreValue}>{value.toFixed(1)}</span>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
